import { ref, readonly } from 'vue';

/**
 * Singleton dialog service. Replaces the browser-native `window.confirm`,
 * `window.alert` and `window.prompt` (which look like ass next to the
 * FRVB-themed app) with promise-returning Vue dialogs that the
 * `DialogHost` component renders at the root of the app.
 *
 * Usage:
 *
 *   const { confirm, alert, prompt } = useDialogs();
 *   const ok = await confirm({
 *       title: 'Delete user?',
 *       message: 'They will no longer be able to sign in.',
 *       confirmText: 'Delete',
 *       tone: 'danger'
 *   });
 *   if (!ok) return;
 *
 * Keep them dead simple — one active dialog at a time. Calling
 * `confirm()` while another is open is fine (the new request just
 * replaces the active dialog and the previous promise is resolved
 * with `false`/null).
 */

export interface ConfirmOpts {
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    /**
     * 'primary' (FRVB green) for safe actions, 'danger' (rose-600)
     * for destructive ones. Default: 'primary'.
     */
    tone?: 'primary' | 'danger';
}

export interface AlertOpts {
    title?: string;
    message: string;
    /** Override the OK button label. Default: 'OK'. */
    confirmText?: string;
    /** Visual tone — useful for "X failed" alerts. */
    tone?: 'primary' | 'danger' | 'warning';
}

export interface PromptOpts {
    title?: string;
    message: string;
    placeholder?: string;
    confirmText?: string;
    cancelText?: string;
    /** Initial value of the input. */
    initial?: string;
    /**
     * Block confirm until the typed value matches this string exactly.
     * Used for "Type OK to confirm" double-check flows.
     */
    requireMatch?: string;
}

interface ConfirmDialog { kind: 'confirm'; opts: ConfirmOpts; resolve: (v: boolean) => void; }
interface AlertDialog   { kind: 'alert';   opts: AlertOpts;   resolve: () => void; }
interface PromptDialog  { kind: 'prompt';  opts: PromptOpts;  resolve: (v: string | null) => void; }
type ActiveDialog = ConfirmDialog | AlertDialog | PromptDialog;

// Module-level singleton state — every call to useDialogs() shares it.
const active = ref<ActiveDialog | null>(null);

function takeOver(): void {
    // If a previous dialog is still open, dismiss it (resolve to the
    // safe default) so the new one can render. This shouldn't happen
    // in practice but keeps the state coherent.
    const prev = active.value;
    if (!prev) return;
    if (prev.kind === 'confirm') (prev as ConfirmDialog).resolve(false);
    else if (prev.kind === 'prompt') (prev as PromptDialog).resolve(null);
    else (prev as AlertDialog).resolve();
}

function confirmAction(opts: ConfirmOpts | string): Promise<boolean> {
    const normalized: ConfirmOpts = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise(resolve => {
        takeOver();
        active.value = { kind: 'confirm', opts: normalized, resolve };
    });
}

function alertAction(opts: AlertOpts | string): Promise<void> {
    const normalized: AlertOpts = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise(resolve => {
        takeOver();
        active.value = { kind: 'alert', opts: normalized, resolve };
    });
}

function promptAction(opts: PromptOpts | string): Promise<string | null> {
    const normalized: PromptOpts = typeof opts === 'string' ? { message: opts } : opts;
    return new Promise(resolve => {
        takeOver();
        active.value = { kind: 'prompt', opts: normalized, resolve };
    });
}

/** Internal — DialogHost calls this when the user picks an action. */
export function _resolveActive(value: boolean | string | null | undefined): void {
    const cur = active.value;
    if (!cur) return;
    active.value = null;          // hide first so DialogHost unmounts cleanly
    if (cur.kind === 'confirm')  (cur as ConfirmDialog).resolve(value as boolean);
    else if (cur.kind === 'prompt') (cur as PromptDialog).resolve(value as string | null);
    else (cur as AlertDialog).resolve();
}

export function useDialogs() {
    return {
        confirm: confirmAction,
        alert:   alertAction,
        prompt:  promptAction,
        /** Read-only ref of the currently-displayed dialog (used by DialogHost). */
        active:  readonly(active)
    };
}
